import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Search, BookOpen, Swords, Users, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const CATEGORIES = [
  { value: "Hand Form", label: "Hand Form", icon: "✋" },
  { value: "Weapons", label: "Weapons", icon: "⚔️" },
  { value: "Sparring Set", label: "Sparring Set", icon: "🥋" },
  { value: "Qigong", label: "Qigong", icon: "🧘" },
];

const PILLARS = [
  { value: "Pillar 1", label: "Pillar 1 – Chou Kup (初級) – Beginner", short: "P1 Chou Kup", color: "bg-gray-500" },
  { value: "Pillar 2", label: "Pillar 2 – Jung Kup (中級) – Intermediate", short: "P2 Jung Kup", color: "bg-blue-600" },
  { value: "Pillar 3", label: "Pillar 3 – Gou Kup (高級) – Advanced", short: "P3 Gou Kup", color: "bg-orange-500" },
  { value: "Pillar 4", label: "Pillar 4 – Gau Lin (Instructor)", short: "P4 Gau Lin", color: "bg-purple-600" },
  { value: "Supplementary", label: "Supplementary Form (Sifu's Selection / Kids Syllabus)", short: "Supplementary", color: "bg-teal-600" },
];

const KIDS_BELTS = [
  { value: "White", label: "White", color: "bg-gray-200 text-gray-800", dot: "bg-gray-200 border border-gray-400" },
  { value: "Yellow", label: "Yellow", color: "bg-yellow-400 text-yellow-900", dot: "bg-yellow-400" },
  { value: "Orange", label: "Orange", color: "bg-orange-400 text-orange-900", dot: "bg-orange-400" },
  { value: "Green", label: "Green", color: "bg-green-500 text-white", dot: "bg-green-500" },
  { value: "Blue", label: "Blue", color: "bg-blue-500 text-white", dot: "bg-blue-500" },
  { value: "Purple", label: "Purple", color: "bg-purple-500 text-white", dot: "bg-purple-500" },
  { value: "Brown", label: "Brown", color: "bg-amber-700 text-white", dot: "bg-amber-700" },
  { value: "Red", label: "Red", color: "bg-red-600 text-white", dot: "bg-red-600" },
  { value: "Red 1 stripe", label: "Red 1S", color: "bg-red-600 text-white", dot: "bg-red-600" },
  { value: "Red 2 stripes", label: "Red 2S", color: "bg-red-600 text-white", dot: "bg-red-600" },
  { value: "Red 3 Stripes", label: "Red 3S", color: "bg-red-700 text-white", dot: "bg-red-700" },
  { value: "Black", label: "Black", color: "bg-gray-900 text-white", dot: "bg-gray-900 border border-white/30" },
];

const parseKidsBelts = (form) => {
  const kb = form?.kids_belt;
  if (!kb || kb === "none") return [];
  return kb.split(",").map(b => b.trim()).filter(Boolean);
};

const toggleBelt = (current, belt) => {
  const belts = current ? current.split(",").map(b => b.trim()).filter(Boolean) : [];
  const idx = belts.indexOf(belt);
  if (idx >= 0) belts.splice(idx, 1); else belts.push(belt);
  return belts.join(",");
};

const LEGACY_PILLAR_MAP = {
  "Foundation": "Pillar 1", "Introduced": "Pillar 1",
  "Intermediate": "Pillar 2", "Advanced": "Pillar 3", "Instructor Level": "Pillar 4",
};

const normalizePillar = (form) => {
  if (form.pillar && (form.pillar.startsWith("Pillar") || form.pillar === "Supplementary")) return form.pillar;
  return LEGACY_PILLAR_MAP[form.library_proficiency] || "Pillar 1";
};

const getPillarColor = (pillar) => {
  const p = PILLARS.find(pp => pp.value === pillar);
  return p?.color || "bg-gray-500";
};

const getPillarShort = (pillar) => {
  const p = PILLARS.find(pp => pp.value === pillar);
  return p?.short || pillar;
};

const getCategoryIcon = (category) => {
  const cat = CATEGORIES.find(c => c.value === category);
  return cat?.icon || "📜";
};

const emptyForm = {
  category: "",
  english_name: "",
  cantonese: "",
  traditional_chinese: "",
  level_category: "",
  sections: [],
  pillar: "Pillar 1",
  form_type: "Core Form",
  kids_belt: "",
  notes: "",
  style: "hung_kuen",
};

export default function FormsLibrary() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPillar, setFilterPillar] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [seeding, setSeeding] = useState(false);
  // v7.4: Style filter (Hung Kuen vs Tai Chi)
  const [styleFilter, setStyleFilter] = useState("hung_kuen");

  useEffect(() => { fetchForms(); }, []);

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/forms`);
      setForms(response.data);
    } catch { toast.error("Failed to load forms"); } finally { setLoading(false); }
  };

  const handleSeedForms = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/forms/seed`);
      toast.success(response.data.message);
      // Migrate pillars after seeding
      await axios.post(`${API}/forms/migrate-pillars`);
      fetchForms();
    } catch { toast.error("Failed to seed forms"); } finally { setSeeding(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.english_name || !formData.cantonese || !formData.traditional_chinese) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const payload = { ...formData, library_proficiency: formData.pillar, kids_belt: formData.kids_belt || "" };
      if (isEditing && selectedForm) {
        await axios.put(`${API}/forms/${selectedForm.id}`, payload);
        toast.success("Form updated");
      } else {
        await axios.post(`${API}/forms`, payload);
        toast.success("Form added");
      }
      setDialogOpen(false);
      setFormData(emptyForm);
      setIsEditing(false);
      setSelectedForm(null);
      fetchForms();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg).join(', ') : "Failed to save");
    }
  };

  const handleEdit = (form) => {
    setSelectedForm(form);
    setFormData({
      category: form.category,
      english_name: form.english_name,
      cantonese: form.cantonese,
      traditional_chinese: form.traditional_chinese,
      level_category: form.level_category || "",
      sections: form.sections || [],
      pillar: normalizePillar(form),
      form_type: form.form_type || "Core Form",
      kids_belt: form.kids_belt || "",
      notes: form.notes || "",
      style: form.style || "hung_kuen",
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDelete = async (formId) => {
    if (!window.confirm("Are you sure you want to delete this form?")) return;
    try {
      await axios.delete(`${API}/forms/${formId}`);
      toast.success("Form deleted");
      fetchForms();
    } catch { toast.error("Failed to delete form"); }
  };

  const filteredForms = forms.filter((form) => {
    const matchesSearch =
      form.english_name.toLowerCase().includes(search.toLowerCase()) ||
      form.cantonese.toLowerCase().includes(search.toLowerCase()) ||
      form.traditional_chinese.includes(search);
    const pillar = normalizePillar(form);
    const matchesPillar = filterPillar === "all" || pillar === filterPillar;
    const matchesTab = activeTab === "all" || form.category === activeTab;
    const matchesStyle = (form.style || "hung_kuen") === styleFilter;
    return matchesSearch && matchesPillar && matchesTab && matchesStyle;
  });

  const styleForms = forms.filter(f => (f.style || "hung_kuen") === styleFilter);
  const handForms = styleForms.filter(f => f.category === "Hand Form");
  const weapons = styleForms.filter(f => f.category === "Weapons");
  const sparringSets = styleForms.filter(f => f.category === "Sparring Set");
  const qigongForms = styleForms.filter(f => f.category === "Qigong");

  return (
    <div data-testid="forms-library-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Loong Fu Tong Forms 龍虎堂套路
            </h1>
            <p className="text-[#A1A1AA] mt-1">{styleFilter === 'hung_kuen' ? 'Hung Gar Kung Fu' : 'Tai Chi Chuan'} Syllabus</p>
          </div>
          <div className="flex gap-2">
            {forms.length === 0 && (
              <Button data-testid="seed-forms-btn" onClick={handleSeedForms} disabled={seeding}
                className="bg-[#D4AF37] hover:bg-[#b8962e] text-black font-semibold uppercase tracking-wider rounded-sm"
                style={{ fontFamily: 'Oswald, sans-serif' }}>
                <RefreshCw className={`w-4 h-4 mr-2 ${seeding ? 'animate-spin' : ''}`} />
                {seeding ? "Seeding..." : "Load Forms"}
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) { setFormData({...emptyForm, style: styleFilter}); setIsEditing(false); setSelectedForm(null); }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-form-btn" onClick={() => setFormData({...emptyForm, style: styleFilter})}
                  className="bg-[#D92323] hover:bg-[#b91c1c] text-white font-semibold uppercase tracking-wider rounded-sm"
                  style={{ fontFamily: 'Oswald, sans-serif' }}>
                  <Plus className="w-4 h-4 mr-2" />Add Form
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-wide flex items-center gap-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {isEditing ? "Edit Form" : "Add New Form"}
                  {isEditing && formData.english_name && (
                    <span className="text-orange-400 text-base normal-case font-medium">{formData.english_name}</span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger data-testid="form-category-select" className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#27272a] border-white/10">
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/10">
                            {cat.icon} {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Pillar Level *</Label>
                    <Select value={formData.pillar} onValueChange={(v) => setFormData({ ...formData, pillar: v })}>
                      <SelectTrigger data-testid="form-pillar-select" className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Select pillar" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#27272a] border-white/10">
                        {PILLARS.map((p) => (
                          <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-[#A1A1AA]">Form Type</Label>
                    <div className="flex gap-4">
                      {["Core Form", "Auxiliary Form"].map(t => (
                        <label key={t} className="flex items-center gap-2 cursor-pointer group">
                          <div onClick={() => setFormData({ ...formData, form_type: t })}
                            className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
                              formData.form_type === t ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/30 group-hover:border-white/50'
                            }`}>
                            {formData.form_type === t && <span className="text-black text-xs font-bold">✓</span>}
                          </div>
                          <span className={`text-sm ${formData.form_type === t ? 'text-white font-medium' : 'text-[#A1A1AA]'}`}>{t}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-[#A1A1AA]">English Name *</Label>
                    <Input data-testid="form-english-name-input" value={formData.english_name}
                      onChange={(e) => setFormData({ ...formData, english_name: e.target.value })}
                      className="bg-white/5 border-white/10 text-white" placeholder="e.g., Taming the Tiger in I-Pattern" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Cantonese (Romanised) *</Label>
                    <Input data-testid="form-cantonese-input" value={formData.cantonese}
                      onChange={(e) => setFormData({ ...formData, cantonese: e.target.value })}
                      className="bg-white/5 border-white/10 text-white" placeholder="e.g., Gung Ji Fuk Fu Kuen" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Traditional Chinese *</Label>
                    <Input data-testid="form-chinese-input" value={formData.traditional_chinese}
                      onChange={(e) => setFormData({ ...formData, traditional_chinese: e.target.value })}
                      className="bg-white/5 border-white/10 text-white" placeholder="e.g., 工字伏虎拳" required />
                  </div>
                </div>
                {/* Kids Syllabus Alignment — Multi-select checkboxes */}
                <div className="space-y-2 pt-2 border-t border-white/10">
                  <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider font-semibold">Kids Syllabus Alignment</Label>
                  <p className="text-[#555] text-[10px]">Select all belt/sash levels this form applies to. A form can have both a Pillar (adults) and multiple Belts (kids).</p>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                    {KIDS_BELTS.map(b => {
                      const selected = formData.kids_belt?.split(",").map(s => s.trim()).includes(b.value);
                      return (
                        <div key={b.value} data-testid={`belt-check-${b.value}`}
                          onClick={() => setFormData({ ...formData, kids_belt: toggleBelt(formData.kids_belt, b.value) })}
                          className={`flex items-center gap-1.5 px-2 py-1.5 rounded-sm cursor-pointer transition-all border text-xs font-medium ${
                            selected ? `${b.color} border-white/40 ring-1 ring-white/30` : 'bg-white/3 text-[#A1A1AA] border-transparent hover:bg-white/5'
                          }`}>
                          <div className={`w-3 h-3 rounded-sm flex-shrink-0 ${selected ? 'bg-white/30' : b.dot}`} />
                          {b.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Notes</Label>
                  <Textarea data-testid="form-notes-input" value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-white/5 border-white/10 text-white min-h-[80px]" placeholder="Additional notes..." />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}
                    className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                  <Button type="submit" data-testid="save-form-btn"
                    className="bg-[#D92323] hover:bg-[#b91c1c] text-white font-semibold uppercase tracking-wider"
                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {isEditing ? "Update" : "Add"} Form
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      </div>

      {/* v7.4: Style Toggle */}
      <div data-testid="style-toggle" className="flex items-center gap-2">
        <button
          data-testid="style-hung-kuen"
          onClick={() => { setStyleFilter("hung_kuen"); setActiveTab("all"); }}
          className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider rounded-sm transition-all ${
            styleFilter === "hung_kuen"
              ? "bg-[#D92323] text-white shadow-lg shadow-red-500/20"
              : "bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10"
          }`}
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          Hung Kuen
        </button>
        <button
          data-testid="style-tai-chi"
          onClick={() => { setStyleFilter("tai_chi"); setActiveTab("all"); }}
          className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider rounded-sm transition-all ${
            styleFilter === "tai_chi"
              ? "bg-teal-600 text-white shadow-lg shadow-teal-500/20"
              : "bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10"
          }`}
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          Tai Chi
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#27272a] rounded-sm"><BookOpen className="w-6 h-6 text-[#D4AF37]" /></div>
            <div>
              <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">Total Forms</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="total-forms-count">{styleForms.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#27272a] rounded-sm text-2xl">{styleFilter === "tai_chi" ? "🧘" : "✋"}</div>
            <div>
              <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">{styleFilter === "tai_chi" ? "Qigong" : "Hand Forms"}</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{styleFilter === "tai_chi" ? qigongForms.length : handForms.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#27272a] rounded-sm"><Swords className="w-6 h-6 text-[#60A5FA]" /></div>
            <div>
              <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">Weapons</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{weapons.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#27272a] rounded-sm"><Users className="w-6 h-6 text-[#22c55e]" /></div>
            <div>
              <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">{styleFilter === "tai_chi" ? "Hand Forms" : "Sparring Sets"}</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{styleFilter === "tai_chi" ? handForms.length : sparringSets.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Filters */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <TabsList className="bg-[#18181b] border border-white/10 p-1 flex-wrap">
            <TabsTrigger value="all" data-testid="tab-all-forms" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
              All ({styleForms.length})
            </TabsTrigger>
            {styleFilter === "tai_chi" && (
              <TabsTrigger value="Qigong" data-testid="tab-qigong" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
                🧘 Qigong ({qigongForms.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="Hand Form" data-testid="tab-hand-forms" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
              ✋ Hand Forms ({handForms.length})
            </TabsTrigger>
            <TabsTrigger value="Weapons" data-testid="tab-weapons" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
              ⚔️ Weapons ({weapons.length})
            </TabsTrigger>
            {styleFilter === "hung_kuen" && (
              <TabsTrigger value="Sparring Set" data-testid="tab-sparring" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
                🥋 Sparring ({sparringSets.length})
              </TabsTrigger>
            )}
          </TabsList>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input data-testid="search-forms-input" value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full sm:w-64 bg-white/5 border-white/10 text-white" placeholder="Search forms..." />
            </div>
            <Select value={filterPillar} onValueChange={setFilterPillar}>
              <SelectTrigger data-testid="filter-pillar-select" className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Pillar" />
              </SelectTrigger>
              <SelectContent className="bg-[#27272a] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Pillars</SelectItem>
                {PILLARS.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">{p.short}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Forms Table */}
        <Card className="bg-[#18181b] border-white/10 rounded-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Category</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">English Name</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Cantonese</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Pillar</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Type</TableHead>
                    {styleFilter === "tai_chi" && <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Skill Focus</TableHead>}
                    {styleFilter === "tai_chi" && <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Min Time</TableHead>}
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={styleFilter === "tai_chi" ? 8 : 6} className="text-center py-8 text-[#A1A1AA]">Loading...</TableCell>
                    </TableRow>
                  ) : filteredForms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={styleFilter === "tai_chi" ? 8 : 6} className="text-center py-8 text-[#A1A1AA]">
                        {forms.length === 0 ? (
                          <div className="space-y-2">
                            <p>No forms in library yet</p>
                            <Button onClick={handleSeedForms} disabled={seeding} className="bg-[#D4AF37] hover:bg-[#b8962e] text-black">
                              {seeding ? "Loading..." : "Load Default Forms"}
                            </Button>
                          </div>
                        ) : "No forms match your filters"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredForms.map((form) => {
                      const pillar = normalizePillar(form);
                      return (
                        <TableRow key={form.id} className="border-white/5 hover:bg-white/5" data-testid={`form-row-${form.id}`}>
                          <TableCell>
                            <span className="text-xl mr-2">{getCategoryIcon(form.category)}</span>
                            <span className="text-[#A1A1AA] text-sm">{form.category}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium text-white">{form.english_name}</span>
                              <span className="text-[#D4AF37] text-xs">{form.traditional_chinese}</span>
                              {form.gate_info && (
                                <span className="text-teal-400 text-[10px] font-semibold px-1.5 py-0.5 bg-teal-500/10 border border-teal-500/20 rounded-sm">
                                  {form.gate_info}
                                </span>
                              )}
                              {parseKidsBelts(form).map(bv => {
                                const belt = KIDS_BELTS.find(b => b.value === bv);
                                return belt ? (
                                  <span key={bv} className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm text-[9px] font-bold ${belt.color}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${belt.dot}`} />{belt.label}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          </TableCell>
                          <TableCell className="text-[#A1A1AA]">{form.cantonese}</TableCell>
                          <TableCell>
                            <span className={`inline-flex px-2 py-1 text-xs rounded-sm text-white ${getPillarColor(pillar)}`}>
                              {getPillarShort(pillar)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs font-medium ${form.form_type === "Core Form" ? "text-[#D4AF37]" : "text-[#A1A1AA]"}`}>
                              {form.form_type || "Core Form"}
                            </span>
                          </TableCell>
                          {styleFilter === "tai_chi" && (
                            <TableCell>
                              <span className="text-xs text-teal-300">{form.skill_focus || '-'}</span>
                            </TableCell>
                          )}
                          {styleFilter === "tai_chi" && (
                            <TableCell>
                              <span className="text-xs text-[#A1A1AA]">{form.min_months ? `${form.min_months}mo` : '-'}</span>
                            </TableCell>
                          )}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(form)}
                                data-testid={`edit-form-${form.id}`} className="h-8 w-8 text-[#A1A1AA] hover:text-white hover:bg-white/10">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(form.id)}
                                data-testid={`delete-form-${form.id}`} className="h-8 w-8 text-[#ef4444] hover:text-[#ef4444] hover:bg-red-500/10">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
